import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment/moment';
import Title from '../../../../commonComponents/Title';
import translate from '../../../../commonComponents/translate';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import CareerFilters from './CareerFilters';
import GlobalSummary from './GlobalSummary';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import Notifications from '../../../../services/notifications';
import Loader from '../../../../services/loader';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import LevelObjectives from './LevelObjectives';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getStartDateAndEndDateInterval } from '../../../../services/dateUtilityFunctions';

function MyCareer(props) {
  const { t, _csrf } = props;

  const loader = useRef(new Loader('#mivitana-my-career'));
  const [isLoading, setLoading] = useState(false);
  const [levelObjectives, setLevelObjectives] = useState([]);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const [globalSummaryData, setGlobalSummaryData] = useState({
    userId: '',
    isActive: false,
    isBinaryPlanActive: false,
    activeUntil: '',
    leftLegComplete: false,
    rightLegComplete: true,
    vaWeek: null,
    monthlyVALeft: null,
    monthlyVARight: null,
    monthlyPersonalVALeft: null,
    monthlyPersonalVARight: null,
    weeklyVALeft: null,
    weeklyVARight: null,
    weeklyAccumulatedLeft: null,
    weeklyAccumulatedRight: null,
    levelIcon: '',
    levelLabel: '',
  });

  const { organization } = useContext(ConfigsContext);
  const { directSelling } = organization;

  // List — initial range in the plan timezone (BRAVON-6325), matching the
  // date-picker presets.
  const [initialStartDate, initialEndDate] =
    getStartDateAndEndDateInterval(directSelling);
  const startOfMonth = initialStartDate.valueOf();
  const today = initialEndDate.valueOf();

  const [filters, setFilters] = useState({
    user: null,
    userNumber: null,
    startDate: startOfMonth,
    endDate: today,
  });

  const handleFiltersChange = useCallback(
    field => value =>
      setFilters(prevState => ({ ...prevState, [field]: value })),
    [setFilters],
  );
  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      startDate,
      endDate,
    }));
  };

  const handleGetGlobalSummary = useCallback(() => {
    axios
      .get('/api/tailor-made/liveoben/summary/global', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setGlobalSummaryData(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleGetObjectives = useCallback(() => {
    axios
      .get('/api/tailor-made/liveoben/level/objectives', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setLevelObjectives(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  useEffect(() => {
    handleGetGlobalSummary();
    handleGetObjectives();
  }, [filters]);

  return (
    <>
      <Title title={t('header_myCareer')}>
        <ContentContainer>
          <SubHeader title={t('header_myCareer')} />
          <TopicBanner />
          <div className="m-content" id="mivitana-my-career">
            <div className="full-page-container-with-subheader">
              <CareerFilters
                t={t}
                onDateChange={handleDateChange}
                onFilterChange={handleFiltersChange}
                filters={filters}
              />
              <div className="career-summaries">
                <GlobalSummary t={t} data={globalSummaryData} />
              </div>
              <div>
                <LevelObjectives data={levelObjectives} />
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

MyCareer.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default translate(MyCareer);
